import React from 'react'
import Fuse from 'fuse.js'
import { Checkbox } from 'antd'

export type ListItemId = string | number

/**
 * This is generic on purpose. This is so it can handle any domain object that has an id and a name.
 * It is important not to start attempting to change this to things like Tag,ReportStatus etc and overcomplicated things.
 *
 * This is so it can be used in multiple places without having to create a new component for each domain object.
 *
 * This is a re-write from shared/webcomponents/zaptic-tag-select.ts to React.
 *
 * At the time of this comment not all of the functionality is here so if you need more then extend this component but you
 * may want to use the above lit component to help with the styling and save some time
 */
export type ListItem = {
    // usually a UUID but it could also be the name if it is unique. Thing like tags would just use the name.
    id: ListItemId
    name: string
}

type Props = {
    items: ListItem[]
    selectedItems: ListItemId[]
    helpText?: string
    onItemsSelected: (items: ListItemId[]) => void
}

export function SearchableItemList(props: Props) {
    const [itemMap, setItemMap] = React.useState<{ [key: ListItemId]: ListItem }>({})
    const [search, setSearch] = React.useState<string>('')
    const [filteredItems, setFilteredItems] = React.useState<ListItem[]>(props.items || [])
    const [selectedIndex, setSelectedIndex] = React.useState<number>(-1)
    const inputRef = React.useRef<HTMLInputElement>(null)

    React.useEffect(() => {
        inputRef.current?.focus()
    }, [])

    React.useEffect(() => {
        setItemMap(props.items.reduce((acc, item) => ({ ...acc, [item.id]: item }), {}))
        setFilteredItems(fuzzyFilter(props.items, search))
    }, [props.items])

    const filterItems = (text: string) => {
        setSearch(text)
        setFilteredItems(fuzzyFilter(props.items, text))
    }

    const handleItemSelect = (item: ListItem, checked: boolean) => {
        if (checked) {
            const newSelectedItems = [...props.selectedItems, item.id]
            props.onItemsSelected(newSelectedItems)
        } else {
            const newSelectedItems = props.selectedItems.filter((selectedItem) => selectedItem !== item.id)
            props.onItemsSelected(newSelectedItems)
        }

        setSearch('')
        inputRef.current?.focus()
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, filteredItems.length - 1))
                break
            case 'ArrowUp':
                event.preventDefault()
                setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0))
                break
            case 'Tab':
            case 'Enter':
                event.preventDefault()
                const item = filteredItems[selectedIndex]
                handleItemSelect(item, !props.selectedItems.includes(item.id))
                return
            default:
                return
        }
    }

    const handleDeselectAll = () => {
        props.onItemsSelected([])
        inputRef.current?.focus()
    }

    if (props.items.length === 0) {
        return (
            <div className="AppCore__SearchableItemList">
                <div className="AppCore__SearchableItemList not-found">
                    <div className="AppCore__SearchableItemList not-found-text">
                        {window.i18n('shared:noSearchResults')}
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="54" height="57" viewBox="0 0 54 57">
                        <g fill="#B2C3DB">
                            <path d="M31.007 18.113c0-7.11-5.785-12.895-12.895-12.895-7.11 0-12.895 5.784-12.895 12.895 0 7.11 5.784 12.895 12.895 12.895 7.11 0 12.895-5.785 12.895-12.895zm-24.208 0c0-6.238 5.075-11.313 11.313-11.313s11.313 5.075 11.313 11.313-5.075 11.313-11.313 11.313S6.799 24.35 6.799 18.113z" />
                            <path d="M11.932 16.064c-.437 0-.791.354-.791.79v1.044c0 .437.354.791.79.791.438 0 .792-.354.792-.791v-1.043c0-.437-.354-.791-.791-.791zM20.663 18.696v1.28c0 .701-.57 1.272-1.271 1.272h-2.56c-.701 0-1.272-.57-1.272-1.272v-1.28c0-1.407 1.145-2.552 2.552-2.552s2.551 1.145 2.551 2.552zm-3.52 0v.97h1.938v-.97c0-.535-.435-.97-.97-.97-.534 0-.969.435-.969.97z" />
                            <path d="M53.997 47.807c-.052-1.674-.773-3.206-2.029-4.315l-5.34-4.711c-.327-.29-.827-.258-1.116.07-.289.327-.258.827.07 1.116l2.546 2.247c-2.728 1.022-4.89 3.185-5.913 5.913l-6.722-7.618c-.302-.342-.286-.863.037-1.186l.458-.458 2.876-2.875.46-.46c.323-.323.844-.34 1.186-.038l2.304 2.033c.328.29.828.258 1.117-.07.289-.327.258-.827-.07-1.116l-2.304-2.033c-.932-.822-2.33-.807-3.248.01l-2.905-2.905.662-.662c.824-.824.824-2.165 0-2.99l-1.688-1.687c1.181-2.404 1.845-5.105 1.845-7.959C36.223 8.126 28.1.001 18.112.001S0 8.126 0 18.113C0 23.31 2.245 28.266 6.16 31.71c.328.289.828.257 1.117-.071.288-.328.256-.828-.072-1.117-3.573-3.144-5.623-7.668-5.623-12.41 0-9.115 7.415-16.53 16.53-16.53 9.114 0 16.53 7.415 16.53 16.53 0 2.793-.698 5.427-1.927 7.737l-.006.013c-1.55 2.906-3.941 5.298-6.848 6.847l-.012.006c-2.31 1.229-4.943 1.926-7.737 1.926-2.81 0-5.588-.72-8.032-2.086-.382-.212-.864-.076-1.077.305-.213.382-.076.864.305 1.077 2.68 1.496 5.724 2.287 8.804 2.287 2.854 0 5.555-.665 7.959-1.846l1.688 1.688c.399.4.93.62 1.494.62.565 0 1.096-.22 1.495-.62l.662-.662 2.906 2.906c-.816.917-.83 2.314-.009 3.245l9.187 10.411c1.108 1.256 2.64 1.977 4.315 2.029l.194.003c1.601 0 3.1-.62 4.239-1.759 1.184-1.184 1.807-2.759 1.755-4.433zm-23.146-14.08l-1.222 1.222c-.1.1-.234.156-.376.156s-.275-.056-.376-.156l-1.362-1.362c2.475-1.51 4.561-3.596 6.07-6.07l1.363 1.361c.207.208.207.545 0 .752l-4.097 4.097zm1.678.56l1.756-1.757 2.901 2.9-1.757 1.757-2.9-2.9zm18.594 16.835c-.873.872-2.034 1.33-3.265 1.293-1.233-.039-2.362-.57-3.178-1.495l-1.246-1.411c.779-2.969 3.107-5.297 6.076-6.076l1.412 1.245c.925.817 1.455 1.945 1.494 3.178.038 1.234-.421 2.393-1.293 3.266z" />
                            <path d="M24.292 18.689c.437 0 .79-.354.79-.791v-1.043c0-.437-.353-.791-.79-.791-.437 0-.791.354-.791.79v1.044c0 .437.354.791.79.791z" />
                        </g>
                    </svg>
                </div>
            </div>
        )
    }

    return (
        <div className="AppCore__SearchableItemList">
            <div className="item-input-wrapper">
                <div className="item-input item-input--limited-height">
                    {props.selectedItems.map((itemId: string) => (
                        <div key={itemId} className="selected">
                            <div className="selected-item">
                                <span className="selected-item-name"> {itemMap[itemId]?.name}</span>
                            </div>
                            <zaptic-delete-icon onClick={() => handleItemSelect(itemMap[itemId], false)} />
                        </div>
                    ))}

                    <div className="AppCore__SearchableItemList__search-wrapper">
                        <zaptic-icon icon-name="magnifier"></zaptic-icon>
                        <input
                            ref={inputRef}
                            type="text"
                            className="input"
                            value={search}
                            onKeyDown={handleKeyDown}
                            onChange={(e) => filterItems(e.target.value)}
                            placeholder={props.selectedItems.length === 0 ? window.i18n('appcore:common.search') : ''}
                            data-testid="item-select-input"
                        />
                    </div>
                </div>
                {props.selectedItems.length > 0 && (
                    <div className="delete-all-icon">
                        <zaptic-delete-icon onClick={() => handleDeselectAll()} data-testid="clear-all-items-select" />
                    </div>
                )}
            </div>

            {props.helpText && <span className="help-text">{props.helpText}</span>}

            <ul>
                {filteredItems.map((item, idx) => (
                    <li key={item.id} className={selectedIndex === idx ? 'item-details item-focus' : 'item-details'}>
                        <Checkbox
                            checked={props.selectedItems.includes(item.id)}
                            onChange={(event) => handleItemSelect(item, event.target.checked)}
                            style={{ fontWeight: 'inherit' }}
                        >
                            <div>
                                <span>{item.name}</span>
                            </div>
                        </Checkbox>
                    </li>
                ))}
            </ul>
        </div>
    )
}

function fuzzyFilter(items: ListItem[], search: string) {
    if (search === '') {
        return items
    }
    const fuse = new Fuse(items, { keys: ['name'], threshold: 0.3 })
    return fuse.search(search).map((result) => result.item)
}
